import { IDentalData } from '@dataTypes/chartingFeature.types'
import { getCuspsImpliedByAtomics } from '@constants/Charting/conditions.schema'
import { Discount } from '@dataTypes/discount.type'
import { resolveBridgeConnectorsInDentalData } from '@utils/bridge-connector.utils'
import {
  IPhaseItems,
  IPhases,
  IProcedureDentalCondition,
  ITreatmentPlan,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'

export {
  resolveProcedureSurfaceArchetype,
  resolveProcedureSurfaceSelectionRules,
} from '@utils/procedure-surface-selection.utils'
export type { ProcedureSurfaceSelectionRules } from '@utils/procedure-surface-selection.utils'

// /^Phase \d+$/ — auto titles only; a deliberate "Phase 5" still matches and may renumber on delete.
const AUTO_PHASE_NAME_PATTERN = /^Phase \d+$/

export function isAutoGeneratedPhaseName(phaseName: string): boolean {
  return AUTO_PHASE_NAME_PATTERN.test(phaseName.trim())
}

/** Renumbers visible phases titled `Phase N` to match visible order; preserves custom titles. */
export function renumberAutoPhaseNames(phasesArray: IPhases[]): IPhases[] {
  let visibleOrdinal = 1
  return phasesArray.map((phase) => {
    if (phase.is_hidden) {
      return phase
    }
    const slot = visibleOrdinal++
    if (isAutoGeneratedPhaseName(phase.phase_name)) {
      return {
        ...phase,
        phase_name: `Phase ${slot}`,
      }
    }
    return phase
  })
}

export function movePhaseByVisibleStep(
  phases: IPhases[],
  originalIdx: number,
  direction: 'up' | 'down',
): IPhases[] {
  const visibleIndices = phases
    .map((p, i) => (!p.is_hidden ? i : -1))
    .filter((i) => i >= 0)
  const pos = visibleIndices.indexOf(originalIdx)
  if (pos < 0) {
    return phases
  }
  const targetPos = direction === 'up' ? pos - 1 : pos + 1
  if (targetPos < 0 || targetPos >= visibleIndices.length) {
    return phases
  }
  const next = [...phases]
  const a = visibleIndices[pos]
  const b = visibleIndices[targetPos]
  ;[next[a], next[b]] = [next[b], next[a]]
  return renumberAutoPhaseNames(next)
}

// --- Drag-and-drop phase rows
export const TX_PLAN_ITEM_DRAG_MIME = 'application/x-enamelai-tx-plan-item'

export type TxPlanItemDragPayload = {
  itemId: string

  fromPhaseOriginalIdx: number
}

export function stringifyTxPlanItemDragPayload(
  payload: TxPlanItemDragPayload,
): string {
  return JSON.stringify(payload)
}

export function parseTxPlanItemDragPayload(
  data: string,
): TxPlanItemDragPayload | null {
  try {
    const parsed = JSON.parse(data) as unknown

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'itemId' in parsed &&
      'fromPhaseOriginalIdx' in parsed &&
      typeof (parsed as TxPlanItemDragPayload).itemId === 'string' &&
      typeof (parsed as TxPlanItemDragPayload).fromPhaseOriginalIdx === 'number'
    ) {
      return parsed as TxPlanItemDragPayload
    }
  } catch {
    return null
  }

  return null
}

export function getVisibleItemIndex(
  items: IPhaseItems[],

  itemId: string,
): number {
  let visible = 0

  for (const it of items) {
    if (it.is_hidden) continue

    if (it.item_id === itemId) return visible

    visible++
  }

  return -1
}

export function adjustInsertBeforeVisibleForSamePhaseReorder(
  fromVisibleIndex: number,
  rawInsertBeforeVisible: number,
): number {
  if (fromVisibleIndex < rawInsertBeforeVisible) {
    return rawInsertBeforeVisible - 1
  }

  return rawInsertBeforeVisible
}

export function visibleInsertBeforeToActualIndex(
  items: IPhaseItems[],
  insertBeforeVisibleIndex: number,
): number {
  const visibleCount = items.filter((x) => !x.is_hidden).length

  const clamped = Math.max(0, Math.min(insertBeforeVisibleIndex, visibleCount))

  let visible = 0

  for (let i = 0; i < items.length; i++) {
    if (!items[i].is_hidden) {
      if (visible === clamped) return i

      visible++
    }
  }

  return items.length
}

export function movePhaseItem(
  phases: IPhases[],
  fromPhaseIndex: number,
  toPhaseIndex: number,
  itemId: string,
  insertBeforeVisibleIndex: number,
): IPhases[] {
  if (
    fromPhaseIndex < 0 ||
    toPhaseIndex < 0 ||
    fromPhaseIndex >= phases.length ||
    toPhaseIndex >= phases.length
  ) {
    return phases
  }

  const fromItems = phases[fromPhaseIndex].items
  const sourceItemIndex = fromItems.findIndex((x) => x.item_id === itemId)
  if (sourceItemIndex === -1) return phases

  const item = fromItems[sourceItemIndex]
  if (item.is_hidden) return phases

  const targetPhaseId = phases[toPhaseIndex].phase_id ?? null
  const moved: IPhaseItems = { ...item, phase_id: targetPhaseId }
  const phasesAfterRemove = phases.map((p, i) =>
    i === fromPhaseIndex
      ? { ...p, items: p.items.filter((_, j) => j !== sourceItemIndex) }
      : p,
  )
  const targetItems = phasesAfterRemove[toPhaseIndex].items
  const actualIndex = visibleInsertBeforeToActualIndex(
    targetItems,

    insertBeforeVisibleIndex,
  )

  return phasesAfterRemove.map((p, i) => {
    if (i !== toPhaseIndex) return p
    const next = [...p.items]
    next.splice(actualIndex, 0, moved)
    return { ...p, items: next }
  })
}

export function getInsertBeforeVisibleIndexFromPointer(
  phaseTableContainer: HTMLElement,
  clientY: number,
): number {
  const rows = phaseTableContainer.querySelectorAll<HTMLTableRowElement>(
    '.e-grid .e-content tr.e-row[data-rowindex]',
  )
  const n = rows.length

  if (n === 0) {
    return 0
  }

  if (n >= 2) {
    const r0 = rows[0].getBoundingClientRect()
    const r1 = rows[1].getBoundingClientRect()
    const mid0 = r0.top + r0.height / 2
    const mid1 = r1.top + r1.height / 2
    const strideMid = mid1 - mid0
    if (strideMid > 2 && strideMid < 150) {
      if (clientY < mid0) {
        return 0
      }

      const idx = Math.floor((clientY - mid0) / strideMid) + 1

      return Math.min(n, idx)
    }
  }

  let insertBefore = n

  for (let i = 0; i < n; i++) {
    const rect = rows[i].getBoundingClientRect()

    if (clientY < rect.top + rect.height / 2) {
      insertBefore = i
      break
    }
  }

  return insertBefore
}

// --- Surface display (grids)

export function getTSFromToothSurfaces(
  toothSurfaces?: Array<{
    area_type: number | null
    area?: string | null
    tooth?: string | null
    surf?: string | null
  }>,
): Array<{
  area_type: number | null
  area?: string
  tooth?: string
  surf?: string
}> {
  if (!toothSurfaces || toothSurfaces.length === 0) return []

  return toothSurfaces.map((ts) => ({
    area_type: ts.area_type ?? null,
    area: ts.area ?? undefined,
    tooth: ts.tooth ?? undefined,
    surf: ts.surf ?? undefined,
  }))
}

export function parseSurfaceString(surface: string | null | undefined): Array<{
  area_type: number | null
  area?: string
  tooth?: string
  surf?: string
}> {
  if (!surface || surface.trim() === '') return []

  const areaTypes = [
    'Upper Arch',
    'Lower Arch',
    'Upper Left',
    'Upper Right',
    'Lower Left',
    'Lower Right',
    'Mouth',
    'None',
  ]

  if (areaTypes.includes(surface)) {
    const areaTypeMap: Record<string, number> = {
      'Upper Arch': 0,
      'Lower Arch': 0,
      Mouth: 2,
      'Upper Left': 3,
      'Upper Right': 3,
      'Lower Left': 3,
      'Lower Right': 3,
      None: 4,
    }

    return [
      {
        area_type: areaTypeMap[surface] ?? null,
        area: surface,
      },
    ]
  }

  const pairs = surface
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s)

  return pairs.map((pair) => {
    if (pair.includes('/')) {
      const [tooth, surf] = pair.split('/')

      return {
        area_type: 1,
        tooth: tooth.trim(),
        surf: surf.trim(),
      }
    }

    return {
      area_type: 1,
      tooth: pair.trim(),
    }
  })
}

// --- Provider validation highlight
export const txPlanProcedureProviderHighlightRowKey = (
  phaseOriginalIdx: number,
  item: IPhaseItems,
): string => {
  const id =
    item.item_id && item.item_id.trim().length > 0
      ? item.item_id.trim()
      : `proc:${item.procedure_id ?? 'unknown'}`

  return `${phaseOriginalIdx}|${id}`
}

export const collectTxPlanMissingProviderRowKeys = (
  phases: IPhases[],
): Set<string> => {
  const keys = new Set<string>()

  phases.forEach((phase, phaseOriginalIdx) => {
    phase.items.forEach((item) => {
      if (item.is_hidden) return

      const pid = item.provider_id?.trim() ?? ''

      if (pid === '') {
        keys.add(txPlanProcedureProviderHighlightRowKey(phaseOriginalIdx, item))
      }
    })
  })

  return keys
}

export type TxPlanProviderRowExclude = {
  phaseOriginalIdx: number
  item: IPhaseItems
}

const txPlanProcedureItemsMatch = (a: IPhaseItems, b: IPhaseItems): boolean =>
  a.item_id === b.item_id && a.procedure_id === b.procedure_id

const txPlanIsExcludedProviderRow = (
  phaseOriginalIdx: number,
  item: IPhaseItems,
  exclude?: TxPlanProviderRowExclude,
): boolean => {
  if (!exclude) return false
  return (
    exclude.phaseOriginalIdx === phaseOriginalIdx &&
    txPlanProcedureItemsMatch(item, exclude.item)
  )
}

const txPlanItemHasEmptyProvider = (item: IPhaseItems): boolean => {
  if (item.is_hidden) return false
  return (item.provider_id?.trim() ?? '') === ''
}

export const countTxPlanEmptyProviderProcedures = (
  phases: IPhases[],
  exclude?: TxPlanProviderRowExclude,
): number => {
  let count = 0
  phases.forEach((phase, phaseOriginalIdx) => {
    phase.items.forEach((item) => {
      if (!txPlanItemHasEmptyProvider(item)) return
      if (txPlanIsExcludedProviderRow(phaseOriginalIdx, item, exclude)) return
      count += 1
    })
  })
  return count
}

export const applyTxPlanProviderToEmptyProcedures = (
  phases: IPhases[],
  providerId: string,
  exclude?: TxPlanProviderRowExclude,
): IPhases[] =>
  phases.map((phase, phaseOriginalIdx) => ({
    ...phase,
    items: phase.items.map((item) => {
      if (!txPlanItemHasEmptyProvider(item)) return item
      if (txPlanIsExcludedProviderRow(phaseOriginalIdx, item, exclude)) {
        return item
      }
      return { ...item, provider_id: providerId }
    }),
  }))

export type TxPlanProviderAssignmentCounts = {
  empty: number
  assigned: number
  visible: number
}

export const countTxPlanProviderAssignments = (
  phases: IPhases[],
): TxPlanProviderAssignmentCounts => {
  let empty = 0
  let assigned = 0
  phases.forEach((phase) => {
    phase.items.forEach((item) => {
      if (item.is_hidden) return
      if ((item.provider_id?.trim() ?? '') === '') empty += 1
      else assigned += 1
    })
  })
  return { empty, assigned, visible: empty + assigned }
}

export const applyTxPlanProviderToVisibleProcedures = (
  phases: IPhases[],
  providerId: string,
): IPhases[] =>
  phases.map((phase) => ({
    ...phase,
    items: phase.items.map((item) => {
      if (item.is_hidden) return item
      return { ...item, provider_id: providerId }
    }),
  }))

export const clearTxPlanVisibleProcedureProviders = (
  phases: IPhases[],
): IPhases[] =>
  phases.map((phase) => ({
    ...phase,
    items: phase.items.map((item) => {
      if (item.is_hidden) return item
      if ((item.provider_id?.trim() ?? '') === '') return item
      return { ...item, provider_id: '' }
    }),
  }))

export const updateTxPlanProcedureProviderOnPhase = (
  phases: IPhases[],
  phaseOriginalIdx: number,
  targetItem: IPhaseItems,
  providerId: string,
): IPhases[] =>
  phases.map((phase, phaseStateIdx) => {
    if (phaseStateIdx !== phaseOriginalIdx) return phase
    return {
      ...phase,
      items: phase.items.map((item) => {
        if (!txPlanProcedureItemsMatch(item, targetItem)) return item
        return { ...item, provider_id: providerId }
      }),
    }
  })

// --- Phase / procedure validation
export const txPlanPhaseHeaderKey = (
  phase: IPhases,
  phaseOriginalIdx: number,
): string => {
  const id = phase.phase_id?.trim() ?? ''
  return id.length > 0 ? id : `phase-${phaseOriginalIdx}`
}

/** Matches Bootstrap accordion `eventKey` in treatment plan phase list. */
export const txPlanPhaseAccordionEventKey = (
  phase: IPhases,
  phaseOriginalIdx: number,
): string => phase.phase_id || `phase-${phaseOriginalIdx}`

export const collectTxPlanVisiblePhaseAccordionKeys = (
  phases: IPhases[],
): string[] =>
  phases
    .map((phase, phaseOriginalIdx) => ({ phase, phaseOriginalIdx }))
    .filter(({ phase }) => !phase.is_hidden)
    .map(({ phase, phaseOriginalIdx }) =>
      txPlanPhaseAccordionEventKey(phase, phaseOriginalIdx),
    )

const isTxPlanPhaseVisibleForShortcut = (
  phase: IPhases,
  phaseVisibility: Record<string, boolean>,
): boolean => {
  if (phase.is_hidden) return false
  const phaseId = phase.phase_id || ''
  return phaseVisibility[phaseId] !== false
}

/** Resolves which phase should receive procedures from the global Enter shortcut. */
export const resolveTxPlanPhaseIndexForProcedureShortcut = (
  phases: IPhases[],
  activeAccordionKeys: string[],
  phaseVisibility: Record<string, boolean>,
): number | null => {
  const visibleEntries = phases
    .map((phase, phaseOriginalIdx) => ({ phase, phaseOriginalIdx }))
    .filter(({ phase }) =>
      isTxPlanPhaseVisibleForShortcut(phase, phaseVisibility),
    )
  if (visibleEntries.length === 0) return null
  const expandedEntries = visibleEntries.filter(({ phase, phaseOriginalIdx }) =>
    activeAccordionKeys.includes(
      txPlanPhaseAccordionEventKey(phase, phaseOriginalIdx),
    ),
  )
  if (expandedEntries.length === 1) {
    return expandedEntries[0].phaseOriginalIdx
  }
  if (expandedEntries.length > 1) {
    for (let i = activeAccordionKeys.length - 1; i >= 0; i -= 1) {
      const key = activeAccordionKeys[i]
      const match = expandedEntries.find(
        ({ phase, phaseOriginalIdx }) =>
          txPlanPhaseAccordionEventKey(phase, phaseOriginalIdx) === key,
      )
      if (match) return match.phaseOriginalIdx
    }
    return expandedEntries[expandedEntries.length - 1].phaseOriginalIdx
  }
  return visibleEntries[0].phaseOriginalIdx
}

/** Remap expanded accordion keys when phase IDs change after save (e.g. temp → server UUID). */
export const remapTxPlanAccordionKeys = (
  previousPhases: IPhases[],
  nextPhases: IPhases[],
  activeKeys: string[],
): string[] => {
  if (activeKeys.length === 0) return activeKeys
  const visiblePrev = previousPhases
    .map((phase, phaseOriginalIdx) => ({ phase, phaseOriginalIdx }))
    .filter(({ phase }) => !phase.is_hidden)
  const visibleNext = nextPhases
    .map((phase, phaseOriginalIdx) => ({ phase, phaseOriginalIdx }))
    .filter(({ phase }) => !phase.is_hidden)
  const remapped: string[] = []
  visiblePrev.forEach(({ phase, phaseOriginalIdx }, visibleIdx) => {
    const prevKey = txPlanPhaseAccordionEventKey(phase, phaseOriginalIdx)
    if (!activeKeys.includes(prevKey)) return
    const nextEntry = visibleNext[visibleIdx]
    if (!nextEntry) return
    const nextKey = txPlanPhaseAccordionEventKey(
      nextEntry.phase,
      nextEntry.phaseOriginalIdx,
    )
    if (!remapped.includes(nextKey)) {
      remapped.push(nextKey)
    }
  })
  return remapped
}

export const txPlanPhaseHasVisibleProcedure = (phase: IPhases): boolean =>
  phase.items.some((item) => !item.is_hidden)

export const txPlanHasVisiblePhaseMissingProcedure = (
  phases: IPhases[],
): boolean =>
  phases.some(
    (phase) => !phase.is_hidden && !txPlanPhaseHasVisibleProcedure(phase),
  )

export const collectTxPlanPhaseKeysMissingProcedure = (
  phases: IPhases[],
): Set<string> => {
  const keys = new Set<string>()
  phases.forEach((phase, phaseOriginalIdx) => {
    if (phase.is_hidden) return
    if (!txPlanPhaseHasVisibleProcedure(phase)) {
      keys.add(txPlanPhaseHeaderKey(phase, phaseOriginalIdx))
    }
  })
  return keys
}

export const collectTxPlanPhaseKeysWithMissingProvider = (
  phases: IPhases[],
): Set<string> => {
  const keys = new Set<string>()
  phases.forEach((phase, phaseOriginalIdx) => {
    if (phase.is_hidden) return
    const hasMissingProvider = phase.items.some((item) => {
      if (item.is_hidden) return false
      return (item.provider_id?.trim() ?? '') === ''
    })
    if (hasMissingProvider) {
      keys.add(txPlanPhaseHeaderKey(phase, phaseOriginalIdx))
    }
  })
  return keys
}

// --- Discount form / API mapping
export const TX_PLAN_DISCOUNT_NONE_ID = ''
export const TX_PLAN_DISCOUNT_CUSTOM_ID = '-1'

const isUuidString = (value: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  return uuidRegex.test(value)
}

export const resolveTxPlanDiscountPercent = (
  selectedDiscountId: string,
  customDiscount: number,
  billingDiscounts: Discount[],
): number => {
  if (
    selectedDiscountId === TX_PLAN_DISCOUNT_NONE_ID ||
    selectedDiscountId === '0'
  ) {
    return 0
  }

  if (selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID) {
    return customDiscount
  }

  const preset = billingDiscounts.find(
    (d) => d.discount_id === selectedDiscountId,
  )

  return preset ? Number(preset.discount_percentage) : 0
}

export type TxPlanDiscountFormValues = {
  selectedDiscountId: string
  customDiscount: number
}

export const txPlanDiscountFormFromPlan = (
  plan: ITreatmentPlan,

  billingDiscounts: Discount[],
): TxPlanDiscountFormValues => {
  const rawId = plan.discount_id as string | number | null | undefined

  let idStr = ''

  if (typeof rawId === 'string') {
    idStr = rawId
  }

  const customVal = typeof plan.discount === 'number' ? plan.discount : 0

  if (idStr && isUuidString(idStr)) {
    const found = billingDiscounts.some((d) => d.discount_id === idStr)

    if (found) {
      return { selectedDiscountId: idStr, customDiscount: 0 }
    }
  }

  if (customVal > 0) {
    return {
      selectedDiscountId: TX_PLAN_DISCOUNT_CUSTOM_ID,
      customDiscount: customVal,
    }
  }

  return { selectedDiscountId: TX_PLAN_DISCOUNT_NONE_ID, customDiscount: 0 }
}

export const txPlanDiscountPayloadFromForm = (
  selectedDiscountId: string,
  customDiscount: number,
): { discount_id: string | null; discount: number | null } => {
  if (selectedDiscountId === TX_PLAN_DISCOUNT_CUSTOM_ID) {
    return {
      discount_id: null,
      discount: customDiscount,
    }
  }

  if (
    selectedDiscountId === TX_PLAN_DISCOUNT_NONE_ID ||
    selectedDiscountId === '0'
  ) {
    return { discount_id: null, discount: null }
  }

  return {
    discount_id: selectedDiscountId,
    discount: null,
  }
}

// --- Dental chart overlay from plan items
const mapSurfaceCodeToClassMapKey = (code: string): string => {
  const mapping: Record<string, string> = {
    m: 'm',
    o: 'o',
    d: 'd',
    b: 'b',
    i: 'i',
    l: 'l',
    p: 'p',
    f: 'b',
  }

  return mapping[code.toLowerCase()] || code.toLowerCase()
}

/** Mirror charting side-panel auto-imply (M+O+P → mp) so cusp overlays match the dental chart. */
const withImpliedCusps = (surfaceCodes: string[]): string[] => {
  const unique = Array.from(new Set(surfaceCodes))
  const implied = getCuspsImpliedByAtomics(new Set(unique)).filter(
    (cusp) => !unique.includes(cusp),
  )
  return [...unique, ...implied]
}

const mapDentalConditionSlug = (slug: string): string => {
  const slugMap: Record<string, string> = {
    implant: 'implant',
    missing: 'missing',
    crown: 'crown',
    filling: 'filling',
    inlay: 'inlay',
    onlay: 'onlay',
    veneer: 'veneer',
    bridge: 'bridge',
    abutment: 'abutment',
    caries: 'caries',
    crack: 'crack',
    fracture: 'fracture',
    attrition: 'attrition',
    abrasion: 'abrasion',
    erosion: 'erosion',
    impacted: 'impacted',
    drifted: 'drifted',
    root_canal: 'root_canal',
    pulpotomy: 'pulpotomy',
    pulpectomy: 'pulpectomy',
    apicoectomy: 'apicoectomy',
    periapical: 'periapical',
    periradicular: 'periradicular',
    supernumerary: 'supernumerary',
    sinus_tract: 'sinus_tract',
    recession: 'recession',
    furcation: 'furcation',
    sealants: 'sealants',
    retainer: 'retainer',
  }

  return slugMap[slug] || slug
}

const parseToothNumbersFromSurface = (
  surface: string | null | undefined,
): number[] => {
  if (!surface) return []

  const parts = surface.split(',').map((p) => p.trim())
  const toothNumbers: number[] = []

  let lastToothNumber: number | null = null

  for (const part of parts) {
    if (part.includes('/')) {
      const [toothStr] = part.split('/')
      const toothNum = parseInt(toothStr.trim(), 10)

      if (!isNaN(toothNum) && toothNum >= 1 && toothNum <= 32) {
        toothNumbers.push(toothNum)
        lastToothNumber = toothNum
      }
    } else {
      const toothNum = parseInt(part.trim(), 10)

      if (
        !isNaN(toothNum) &&
        toothNum >= 1 &&
        toothNum <= 32 &&
        (toothNum > 9 || lastToothNumber === null)
      ) {
        if (toothNum <= 9 && lastToothNumber !== null) {
          if (lastToothNumber !== null) {
            toothNumbers.push(lastToothNumber)
          }
        } else {
          toothNumbers.push(toothNum)
          lastToothNumber = toothNum
        }
      } else if (lastToothNumber !== null) {
        toothNumbers.push(lastToothNumber)
      }
    }
  }

  return Array.from(new Set(toothNumbers))
}

const parseSurfacesFromSurface = (
  surface: string | null | undefined,
): string[] => {
  if (!surface) return []

  const parts = surface.split(',').map((p) => p.trim())
  const surfaces: string[] = []

  for (const part of parts) {
    if (part.includes('/')) {
      const [, ...surfaceParts] = part.split('/')
      const surfaceStrings = surfaceParts
        .map((s) => s.trim())
        .filter((s) => s.length > 0)

      for (const surfStr of surfaceStrings) {
        const chars = surfStr.split('')
        surfaces.push(...chars.map(mapSurfaceCodeToClassMapKey))
      }
    } else {
      const isLikelySurfaceCode =
        part.length <= 2 ||
        /^[A-Za-z]+$/.test(part) ||
        (part.length === 1 && /^\d$/.test(part))

      if (isLikelySurfaceCode) {
        const chars = part.split('')
        surfaces.push(...chars.map(mapSurfaceCodeToClassMapKey))
      }
    }
  }

  return surfaces.filter((s) => s.length > 0)
}

const parseToothNumbersFromToothSurfaces = (
  toothSurfaces:
    | Array<{
        area_type: number | null
        tooth?: string | null
      }>
    | null
    | undefined,
): number[] => {
  if (!toothSurfaces || toothSurfaces.length === 0) return []

  const toothNumbers: number[] = []

  for (const ts of toothSurfaces) {
    if (ts.area_type === 1 && ts.tooth) {
      const toothNum = parseInt(ts.tooth.trim(), 10)

      if (!isNaN(toothNum) && toothNum >= 1 && toothNum <= 32) {
        toothNumbers.push(toothNum)
      }
    }
  }

  return Array.from(new Set(toothNumbers))
}

type ResolvedProcedureDentalCondition = {
  dental_condition: NonNullable<IProcedureDentalCondition['dental_condition']>
  default_material: string | null
  default_type: string | null
  default_surface: string | null
}

type ToothSurfaceTarget = {
  toothNumber: number
  surfaceCodes: string[]
}

export const resolveProcedureDentalConditionsWithNestedCondition = (
  ...sources: Array<IProcedureDentalCondition[] | undefined>
): IProcedureDentalCondition[] => {
  const seen = new Set<string>()
  const result: IProcedureDentalCondition[] = []
  for (const source of sources) {
    for (const mapping of source ?? []) {
      if (!mapping.dental_condition) {
        continue
      }
      const key =
        mapping.id ?? mapping.dental_condition_id ?? mapping.dental_condition.id
      if (key && seen.has(key)) {
        continue
      }
      if (key) {
        seen.add(key)
      }
      result.push(mapping)
    }
  }
  return result
}

const resolveProcedureDentalConditionsForItem = (
  item: IPhaseItems,
): ResolvedProcedureDentalCondition[] => {
  return resolveProcedureDentalConditionsWithNestedCondition(
    item.proceduredentalcondition,
    item.procedure_settings?.procedure?.procedure_dental_conditions,
  ).map((mapping) => ({
    dental_condition: mapping.dental_condition!,
    default_material: mapping.default_material ?? null,
    default_type: mapping.default_type ?? null,
    default_surface: mapping.default_surface ?? null,
  }))
}

const parseSurfaceCodesFromSurfString = (
  surf: string | null | undefined,
): string[] => {
  if (!surf?.trim()) {
    return []
  }
  return surf
    .trim()
    .split('')
    .map(mapSurfaceCodeToClassMapKey)
    .filter((code) => code.length > 0)
}

const getToothSurfaceTargetsFromItem = (
  item: IPhaseItems,
): ToothSurfaceTarget[] => {
  if (item.tooth_surfaces && item.tooth_surfaces.length > 0) {
    const surfacesByTooth = new Map<number, string[]>()
    for (const toothSurface of item.tooth_surfaces) {
      if (toothSurface.area_type !== 1 || !toothSurface.tooth) {
        continue
      }
      const toothNumber = parseInt(toothSurface.tooth.trim(), 10)
      if (Number.isNaN(toothNumber) || toothNumber < 1 || toothNumber > 32) {
        continue
      }
      const existing = surfacesByTooth.get(toothNumber) ?? []
      if (toothSurface.surf) {
        existing.push(...parseSurfaceCodesFromSurfString(toothSurface.surf))
      }
      surfacesByTooth.set(toothNumber, existing)
    }
    if (surfacesByTooth.size > 0) {
      return Array.from(surfacesByTooth.entries()).map(
        ([toothNumber, surfaceCodes]) => ({
          toothNumber,
          surfaceCodes: withImpliedCusps(surfaceCodes),
        }),
      )
    }
  }
  const toothNumbers = parseToothNumbersFromSurface(item.surface || null)
  const surfaceCodes = withImpliedCusps(
    parseSurfacesFromSurface(item.surface || null),
  )
  return toothNumbers.map((toothNumber) => ({
    toothNumber,
    surfaceCodes,
  }))
}

const convertDentalConditionToIDentalData = (
  toothNumber: number,

  dentalCondition: {
    slug: string
    default_material?: string | null
    default_type?: string | null
    default_surface?: string | null
  },

  surfaceCodes?: string[],
): IDentalData[] => {
  const condition = mapDentalConditionSlug(dentalCondition.slug)
  const material = dentalCondition.default_material || null
  const type = dentalCondition.default_type || null
  const defaultSurface = dentalCondition.default_surface || null
  const surfaces =
    surfaceCodes && surfaceCodes.length > 0
      ? surfaceCodes
      : defaultSurface
        ? [defaultSurface]
        : [null]

  return surfaces.map((surface) => ({
    tooth_number: toothNumber.toString(),
    condition,
    type,
    material,
    surface: surface || null,
    recurrent: false,
  }))
}

export const extractDentalConditionsFromTreatmentPlanItems = (
  treatmentPlanItems: IPhaseItems[],
): IDentalData[] => {
  const dentalDataArray: IDentalData[] = []

  for (const item of treatmentPlanItems) {
    if (item.is_hidden) {
      continue
    }

    const procedureDentalConditions =
      resolveProcedureDentalConditionsForItem(item)
    if (procedureDentalConditions.length === 0) {
      continue
    }

    const toothSurfaceTargets = getToothSurfaceTargetsFromItem(item)
    if (toothSurfaceTargets.length === 0) {
      continue
    }

    for (const procDentalCondition of procedureDentalConditions) {
      const dentalCondition = procDentalCondition.dental_condition
      for (const { toothNumber, surfaceCodes } of toothSurfaceTargets) {
        const dentalDataItems = convertDentalConditionToIDentalData(
          toothNumber,
          {
            slug: dentalCondition.slug,
            default_material: procDentalCondition.default_material,
            default_type: procDentalCondition.default_type,
            default_surface: procDentalCondition.default_surface,
          },
          surfaceCodes.length > 0 ? surfaceCodes : undefined,
        )
        dentalDataArray.push(...dentalDataItems)
      }
    }
  }

  return dentalDataArray
}

const getToothNumberFromDentalData = (d: IDentalData): number => {
  const raw = d.tooth_number
  const n = parseInt(String(raw).trim(), 10)

  return Number.isFinite(n) ? n : -1
}

const mergeDentalDataForSingleTooth = (
  chartForTooth: IDentalData[],
  tpForTooth: IDentalData[],
): IDentalData[] => {
  const merged: IDentalData[] = [...tpForTooth]

  for (const chartData of chartForTooth) {
    const hasConflict = tpForTooth.some(
      (tpData) =>
        tpData.condition === chartData.condition &&
        tpData.surface === chartData.surface &&
        tpData.material === chartData.material &&
        tpData.type === chartData.type,
    )

    if (!hasConflict) {
      merged.push(chartData)
    }
  }

  return merged
}

/** Pass the same `tpDental` array across chart edits to keep plan row objects stable. */
export const mergeChartDentalDataWithPlanRows = (
  chartDentalData: IDentalData[],
  tpDental: IDentalData[],
): IDentalData[] => {
  const result: IDentalData[] = []

  for (let toothId = 1; toothId <= 32; toothId += 1) {
    const tpForTooth = tpDental.filter(
      (d) => getToothNumberFromDentalData(d) === toothId,
    )

    const chartForTooth = chartDentalData.filter(
      (d) => getToothNumberFromDentalData(d) === toothId,
    )

    result.push(...mergeDentalDataForSingleTooth(chartForTooth, tpForTooth))
  }

  return resolveBridgeConnectorsInDentalData(result)
}

export const mergeChartDentalDataWithTreatmentPlanItems = (
  chartDentalData: IDentalData[],
  treatmentPlanItems: IPhaseItems[],
): IDentalData[] =>
  mergeChartDentalDataWithPlanRows(
    chartDentalData,
    extractDentalConditionsFromTreatmentPlanItems(treatmentPlanItems),
  )

export const getCompletedTreatmentPlanItemsForPatient = (
  treatmentPlans: ITreatmentPlan[],
  patientId: string,
): IPhaseItems[] =>
  treatmentPlans
    .filter((plan) => plan.patient_id === patientId)
    .flatMap((plan) =>
      (plan.phases ?? [])
        .filter(
          (phase) =>
            !phase.is_hidden && phase.status === TX_PLAN_STATUS.COMPLETED,
        )
        .flatMap((phase) => phase.items.filter((item) => !item.is_hidden)),
    )

/** Dental rows for completed procedures, or null when none should be shown. */
export const getCompletedProcedureDentalRows = (
  treatmentPlans: ITreatmentPlan[],
  patientId: string | undefined,
  showCompletedProcedures: boolean,
): IDentalData[] | null => {
  if (!showCompletedProcedures || !patientId) {
    return null
  }
  const completedItems = getCompletedTreatmentPlanItemsForPatient(
    treatmentPlans,
    patientId,
  )
  if (completedItems.length === 0) {
    return null
  }
  return extractDentalConditionsFromTreatmentPlanItems(completedItems)
}

export const mergeDentalChartWithCompletedProcedures = (
  chartDentalData: IDentalData[],
  treatmentPlans: ITreatmentPlan[],
  patientId: string | undefined,
  showCompletedProcedures: boolean,
): IDentalData[] => {
  const completedRows = getCompletedProcedureDentalRows(
    treatmentPlans,
    patientId,
    showCompletedProcedures,
  )
  return completedRows
    ? mergeChartDentalDataWithPlanRows(chartDentalData, completedRows)
    : chartDentalData
}

export const getTreatmentPlanReferencedToothIds = (
  treatmentPlanItems: IPhaseItems[],
): ReadonlySet<number> => {
  const ids = new Set<number>()

  for (const item of treatmentPlanItems) {
    if (item.is_hidden) {
      continue
    }

    let toothNumbers: number[] = []

    if (item.tooth_surfaces && item.tooth_surfaces.length > 0) {
      toothNumbers = parseToothNumbersFromToothSurfaces(item.tooth_surfaces)
    }

    if (toothNumbers.length === 0) {
      toothNumbers = parseToothNumbersFromSurface(item.surface || null)
    }

    toothNumbers.forEach((n) => {
      ids.add(n)
    })
  }

  return ids
}
